import { buildCuttingStudy, linkedMaterialCostLines, materialStudyDetails, materialStudyUnit, sheetMaterialLines } from "./simulationStudy.js";
import { buildCostLines, calculateSimulationProduct } from "@/lib/api/manufacturingSimulation";
import { calculateSheetTotals, fetchCostSheetsByIds, formatMoney } from "@/lib/api/manufacturing";
import { SIMULATION_SIZES } from "@/lib/constants";

const SIZE_FIELDS = {
  P: "quantity_p",
  M: "quantity_m",
  L: "quantity_l",
  XL: "quantity_xl",
  "2XL": "quantity_2xl",
  "3XL": "quantity_3xl",
  "4XL": "quantity_4xl",
  "5XL": "quantity_5xl",
};

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function productImage(product) {
  if (!product) return "";
  const images = product.images || [];
  return images[product.main_image_index || 0] || images[0] || "";
}

function resolveColor(colorId, colorSettings) {
  const data = colorSettings?.[colorId];
  return {
    description: data?.description || colorId,
    hex: data?.hex || "#d1d5db",
  };
}

function normalizeColorLines(colorLines = [], colorSettings) {
  return [...colorLines]
    .sort((left, right) => Number(left.sort_order || 0) - Number(right.sort_order || 0))
    .map((line) => {
      const { description, hex } = resolveColor(line.color, colorSettings);
      const sizes = Object.fromEntries(SIMULATION_SIZES.map((size) => [size, Number(line[SIZE_FIELDS[size]] || 0)]));
      const total = SIMULATION_SIZES.reduce((sum, size) => sum + sizes[size], 0);
      return {
        color: line.color,
        garment_color: line.garment_color || line.color,
        variant_id: line.variant_id || null,
        material_colors: line.material_colors || {},
        description,
        hex,
        contrast_1: line.contrast_1 || "",
        contrast_2: line.contrast_2 || "",
        contrast_3: line.contrast_3 || "",
        sizes,
        total,
      };
    });
}

function buildCostSheetLineLookups(costSheet) {
  const processByMaterialId = new Map();
  const supplierByMaterialId = new Map();
  const supplierByProcessLineId = new Map();
  if (!costSheet) {
    return { processByMaterialId, supplierByMaterialId, supplierByProcessLineId };
  }

  for (const processLine of costSheet.process_lines || []) {
    supplierByProcessLineId.set(processLine.id, processLine.supplier?.name || "");
    const processName = processLine.process?.description || "Proceso";
    for (const material of processLine.material_lines || []) {
      processByMaterialId.set(material.id, { processName, processLineId: processLine.id, processId: processLine.process_id || processLine.process?.id, processCode: processLine.process?.legacy_code });
      supplierByMaterialId.set(material.id, material.material_supplier?.supplier?.name || "");
    }
  }

  for (const material of costSheet.material_lines || []) {
    if (!processByMaterialId.has(material.id) && material.process_line_id) {
      const processLine = (costSheet.process_lines || []).find((line) => line.id === material.process_line_id);
      processByMaterialId.set(material.id, {
        processName: processLine?.process?.description || "Proceso",
        processLineId: material.process_line_id,
        processId: processLine?.process_id || processLine?.process?.id,
        processCode: processLine?.process?.legacy_code,
      });
    }
    if (!supplierByMaterialId.has(material.id)) {
      supplierByMaterialId.set(material.id, material.material_supplier?.supplier?.name || "");
    }
  }

  return { processByMaterialId, supplierByMaterialId, supplierByProcessLineId };
}

function enrichCostLinesFromSheet(costLines = [], costSheet) {
  const { processByMaterialId, supplierByMaterialId, supplierByProcessLineId } = buildCostSheetLineLookups(costSheet);
  const materialsById = new Map(sheetMaterialLines(costSheet || {}).map((line) => [line.id, line]));

  return costLines.map((line) => {
    if (line.line_type === "material") {
      const processInfo = processByMaterialId.get(line.source_line_id);
      return {
        ...line,
        ...materialStudyDetails(materialsById.get(line.source_line_id)),
        unit: materialStudyUnit(materialsById.get(line.source_line_id), costSheet) || line.unit,
        process_id: processInfo?.processId || null,
        process_code: processInfo?.processCode || "",
        process_source_line_id: line.process_source_line_id || processInfo?.processLineId || null,
        process_name_snapshot: line.process_name_snapshot?.trim() ? line.process_name_snapshot : processInfo?.processName || null,
        supplier_name_snapshot: line.supplier_name_snapshot?.trim()
          ? line.supplier_name_snapshot
          : supplierByMaterialId.get(line.source_line_id) || null,
      };
    }
    if (line.line_type === "process") {
      return {
        ...line,
        notes: costSheet?.process_lines?.find((p) => p.id === line.source_line_id)?.notes || "",
        supplier_code: costSheet?.process_lines?.find((p) => p.id === line.source_line_id)?.supplier?.erp_code || "",
        supplier_name_snapshot: line.supplier_name_snapshot?.trim()
          ? line.supplier_name_snapshot
          : supplierByProcessLineId.get(line.source_line_id) || null,
      };
    }
    return line;
  });
}

function normalizeCostLines(costLines = [], totalQuantity = 0) {
  const lines = [...costLines].sort((left, right) => Number(left.sort_order || 0) - Number(right.sort_order || 0));
  const map = (line) => {
    const quantityPerUnit = Number(line.quantity_per_unit || 0);
    const wastePct = Number(line.waste_pct || 0);
    return {
      ...materialStudyDetails(),
      ...line,
      name: line.name_snapshot || "",
      unit: line.unit || "",
      quantity_per_unit: quantityPerUnit,
      waste_pct: wastePct,
      unit_cost: Number(line.unit_cost || 0),
      total_cost: Number(line.total_cost || 0),
      unit_line_cost: totalQuantity > 0 ? Number(line.total_cost || 0) / totalQuantity : 0,
      total_quantity: Number((quantityPerUnit * (1 + wastePct / 100) * totalQuantity).toFixed(4)),
      process_name: line.process_name_snapshot || "",
      process_source_line_id: line.process_source_line_id || null,
      supplier_name: line.supplier_name_snapshot || "",
    };
  };
  const mapProcess = (line) => ({
    ...line,
    name: line.name_snapshot || "",
    unit: "",
    quantity_per_unit: Number(line.quantity_per_unit || 0),
    waste_pct: 0,
    unit_cost: Number(line.unit_cost || 0),
    total_cost: Number(line.total_cost || 0),
    unit_line_cost: totalQuantity > 0 ? Number(line.total_cost || 0) / totalQuantity : 0,
    total_quantity: totalQuantity,
    process_name: "",
    process_source_line_id: null,
    supplier_name: line.supplier_name_snapshot || "",
    line_kind: "process",
  });
  const mapMaterial = (line) => ({
    ...map(line),
    line_kind: "material",
  });
  return {
    processes: lines.filter((line) => line.line_type === "process").map(mapProcess),
    materials: lines.filter((line) => line.line_type === "material").map(mapMaterial),
  };
}

function normalizeItem({ item, product, colorSettings, validated, costSheet }) {
  const totalQuantity = Number(item.total_quantity || 0);
  const colorLines = normalizeColorLines(item.color_lines, colorSettings);
  const sizeTotals = Object.fromEntries(
    SIMULATION_SIZES.map((size) => [size, colorLines.reduce((sum, line) => sum + line.sizes[size], 0)])
  );
  const costLines = enrichCostLinesFromSheet(item.cost_lines, costSheet);
  const excludedProcesses = new Set((costSheet?.process_lines || [])
    .filter((line) => line.process?.excludes_manufacturing).map((line) => line.id));
  const visibleCostLines = costLines.filter((line) => !(line.process?.excludes_manufacturing ?? line.excludes_manufacturing)
    && !excludedProcesses.has(line.line_type === "process" ? line.source_line_id : line.process_source_line_id));
  const { processes, materials } = normalizeCostLines(visibleCostLines, totalQuantity);
  const additionalMaterials = enrichCostLinesFromSheet(
    linkedMaterialCostLines(costSheet, costLines, totalQuantity), costSheet
  );
  return {
    product_id: item.product_id || product?.id || null,
    code: item.product_code_snapshot || product?.code || "",
    description: item.description_snapshot || product?.description || "",
    season: item.season_snapshot || product?.season?.descrip || product?.temporada || "",
    composicion: product?.composicion || "",
    manufacturer_reference: item.manufacturer_reference || "",
    pattern_code: item.pattern_code || "",
    notes: item.notes || "",
    validated: validated ?? null,
    image: productImage(product),
    colorLines,
    colorSettings,
    sizeTotals,
    totalQuantity,
    processes,
    materials,
    supplierProcessOrder: Object.fromEntries((costSheet?.process_lines || []).map((process) => {
      const destinations = (costSheet.process_lines || []).filter((destination) =>
        !destination.process?.excludes_manufacturing && destination.process?.albaran_material_process_id
        && destination.process.albaran_material_process_id === (process.process_id || process.process?.id));
      const orderSources = process.process?.excludes_manufacturing && destinations.length ? destinations : [process];
      return [process.id, Math.min(...orderSources.map((line) => Number(line.sort_order || 0)))];
    })),
    supplierMaterials: [...normalizeCostLines(costLines, totalQuantity).materials, ...normalizeCostLines(additionalMaterials, totalQuantity).materials],
    cutting: buildCuttingStudy(costSheet, colorLines),
    unitProcessCost: Number(item.unit_process_cost || 0),
    unitMaterialCost: Number(item.unit_material_cost || 0),
    unitTotalCost: Number(item.unit_total_cost || 0),
    totalProcessCost: Number(item.total_process_cost || 0),
    totalMaterialCost: Number(item.total_material_cost || 0),
    totalCost: Number(item.total_cost || 0),
  };
}

export function buildStudyItemFromEntry(entry, colorSettings = {}) {
  const calculated = calculateSimulationProduct(entry.product, entry.costSheet, entry.quantities, entry.details);
  const totals = calculateSheetTotals(entry.costSheet || {});
  const item = calculated.item || {
    ...entry.details,
    product_code_snapshot: entry.product.code,
    description_snapshot: entry.product.description,
    total_quantity: 0, color_lines: [],
    cost_lines: buildCostLines(entry.costSheet, 0),
    unit_process_cost: totals.processOwnTotal,
    unit_material_cost: totals.materialTotal,
    unit_total_cost: totals.total,
  };
  const model = normalizeItem({ item, product: entry.product, colorSettings, validated: entry.hasValidatedCostSheet, costSheet: entry.costSheet });
  if (!calculated.item) {
    model.materials.forEach((line) => { line.unit_line_cost = line.quantity_per_unit * (1 + line.waste_pct / 100) * line.unit_cost; });
    model.processes.forEach((line) => { line.unit_line_cost = line.quantity_per_unit * line.unit_cost; });
  }
  return model;
}

export function buildPrintModelFromEntries({ simulationDate, description, notes } = {}, entries = [], colorSettings = {}) {
  const items = entries
    .map((entry) => {
      const { item } = calculateSimulationProduct(entry.product, entry.costSheet, entry.quantities, entry.details);
      if (!item) return null;
      return normalizeItem({
        item,
        product: entry.product,
        colorSettings,
        validated: entry.hasValidatedCostSheet,
        costSheet: entry.costSheet,
      });
    })
    .filter(Boolean);
  return {
    simulationDate,
    description,
    notes,
    items,
    totals: {
      total_quantity: items.reduce((sum, item) => sum + item.totalQuantity, 0),
      process_total: items.reduce((sum, item) => sum + item.totalProcessCost, 0),
      material_total: items.reduce((sum, item) => sum + item.totalMaterialCost, 0),
      grand_total: items.reduce((sum, item) => sum + item.totalCost, 0),
    },
  };
}

export function buildPrintModelFromRecord(simulation = {}, colorSettings = {}, costSheetsById = new Map()) {
  const items = (simulation.items || []).map((item) =>
    normalizeItem({
      item,
      product: item.product,
      colorSettings,
      validated: null,
      costSheet: costSheetsById.get(item.cost_sheet_id) || null,
    })
  );
  return {
    simulationDate: simulation.simulation_date,
    description: simulation.description,
    notes: simulation.notes,
    items,
    totals: {
      total_quantity: Number(simulation.total_quantity || 0),
      process_total: Number(simulation.process_total || 0),
      material_total: Number(simulation.material_total || 0),
      grand_total: Number(simulation.grand_total || 0),
    },
  };
}

export async function buildPrintModelFromRecordAsync(simulation = {}, colorSettings = {}) {
  const costSheetIds = (simulation.items || []).map((item) => item.cost_sheet_id).filter(Boolean);
  const { data: costSheetsById, error } = await fetchCostSheetsByIds(costSheetIds);
  if (error) throw error;
  return buildPrintModelFromRecord(simulation, colorSettings, costSheetsById || new Map());
}


export { buildNeedsByProcess } from "./simulationProcessNeeds.js";

export { buildNeedsBySupplier } from "./simulationSupplierNeeds.js";

export function formatDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("es-ES");
}

function renderMatrix(item) {
  const head = `
    <tr>
      <th class="text-left">Color</th>
      <th class="text-left">Contr. 1</th>
      <th class="text-left">Contr. 2</th>
      <th class="text-left">Contr. 3</th>
      ${SIMULATION_SIZES.map((size) => `<th>${esc(size)}</th>`).join("")}
      <th>Total</th>
    </tr>`;
  const rows = item.colorLines
    .map(
      (line) => `
    <tr>
      <td class="text-left"><span class="swatch" style="background:${esc(line.hex)}"></span>${esc(line.description)}</td>
      <td class="text-left">${esc(line.contrast_1)}</td>
      <td class="text-left">${esc(line.contrast_2)}</td>
      <td class="text-left">${esc(line.contrast_3)}</td>
      ${SIMULATION_SIZES.map((size) => `<td>${line.sizes[size] || ""}</td>`).join("")}
      <td class="strong">${line.total}</td>
    </tr>`
    )
    .join("");
  const footer = `
    <tr class="foot">
      <td class="text-left strong">Total prendas</td>
      <td colspan="3"></td>
      ${SIMULATION_SIZES.map((size) => `<td class="strong">${item.sizeTotals[size] || 0}</td>`).join("")}
      <td class="strong">${item.totalQuantity}</td>
    </tr>`;
  return `<table class="matrix"><thead>${head}</thead><tbody>${rows}</tbody><tfoot>${footer}</tfoot></table>`;
}

function renderMaterials(item) {
  if (!item.materials.length) return `<p class="muted">El escandallo no contiene materiales.</p>`;
  const rows = item.materials
    .map(
      (line) => `
    <tr>
      <td class="text-left">${esc(line.name)}</td>
      <td>${esc(line.unit)}</td>
      <td class="num">${line.quantity_per_unit}</td>
      <td class="num">${line.waste_pct}%</td>
      <td class="num">${formatMoney(line.unit_cost)}</td>
      <td class="num">${formatMoney(line.unit_line_cost)}</td>
      <td class="num">${formatMoney(line.total_cost)}</td>
    </tr>`
    )
    .join("");
  return `
    <table class="lines">
      <thead><tr>
        <th class="text-left">Material</th><th>Unidad</th><th class="num">Consumo/prenda</th>
        <th class="num">Merma</th><th class="num">Precio</th><th class="num">Coste/prenda</th><th class="num">Importe total</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
}

function renderProcesses(item) {
  if (!item.processes.length) return `<p class="muted">El escandallo no contiene procesos.</p>`;
  const rows = item.processes
    .map(
      (line) => `
    <tr>
      <td class="text-left">${esc(line.name)}</td>
      <td class="num">${formatMoney(line.unit_line_cost)}</td>
      <td class="num">${formatMoney(line.total_cost)}</td>
    </tr>`
    )
    .join("");
  return `
    <table class="lines">
      <thead><tr><th class="text-left">Proceso</th><th class="num">Coste/prenda</th><th class="num">Importe total</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
}

function renderArticle(item, index) {
  const escandallo = item.validated === null ? "" : `<div><span class="lbl">Escandallo</span>${item.validated ? "Validado" : "Sin validar"}</div>`;
  const image = item.image
    ? `<img class="product-img" src="${esc(item.image)}" alt="${esc(item.description)}" />`
    : `<div class="product-img placeholder">Sin imagen</div>`;
  return `
  <section class="article ${index === 0 ? "first" : ""}">
    <div class="article-head">
      ${image}
      <div class="article-data">
        <h2>${esc(item.code)} · ${esc(item.description)}</h2>
        <div class="data-grid">
          <div><span class="lbl">Temporada</span>${esc(item.season) || "—"}</div>
          <div><span class="lbl">Composición</span>${esc(item.composicion) || "—"}</div>
          <div><span class="lbl">Ref. fabricante</span>${esc(item.manufacturer_reference) || "—"}</div>
          <div><span class="lbl">Código patrón</span>${esc(item.pattern_code) || "—"}</div>
          ${escandallo}
          <div><span class="lbl">Observaciones</span>${esc(item.notes) || "—"}</div>
        </div>
      </div>
    </div>

    <h3>Cantidades por color y talla</h3>
    ${renderMatrix(item)}

    <h3>Materiales necesarios</h3>
    ${renderMaterials(item)}

    <h3>Procesos de fabricación</h3>
    ${renderProcesses(item)}

    <div class="totals">
      <div><span class="lbl">Total prendas</span><b>${item.totalQuantity}</b></div>
      <div><span class="lbl">Coste material/prenda</span><b>${formatMoney(item.unitMaterialCost)}</b></div>
      <div><span class="lbl">Coste proceso/prenda</span><b>${formatMoney(item.unitProcessCost)}</b></div>
      <div><span class="lbl">Coste/prenda</span><b>${formatMoney(item.unitTotalCost)}</b></div>
      <div class="grand"><span class="lbl">Coste total artículo</span><b>${formatMoney(item.totalCost)}</b></div>
    </div>
  </section>`;
}

export function renderSimulationPrintHtml(model) {
  const articles = model.items.map((item, index) => renderArticle(item, index)).join("");
  const title = `Simulación de orden de fabricación`;
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${esc(model.description || title)}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 11px; margin: 0; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  h2 { font-size: 14px; margin: 0 0 6px; }
  h3 { font-size: 12px; margin: 14px 0 6px; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
  .muted { color: #666; font-style: italic; }
  .lbl { display: block; font-size: 9px; text-transform: uppercase; color: #777; letter-spacing: .03em; }
  .doc-head { border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 10px; }
  .doc-meta { display: flex; flex-wrap: wrap; gap: 18px; margin-top: 4px; }
  .doc-totals { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 8px; }
  .doc-totals div { border: 1px solid #ccc; border-radius: 4px; padding: 4px 10px; }
  .doc-totals b { font-size: 13px; }
  .article { margin-bottom: 16px; }
  .article.first { break-before: auto; }
  .article:not(.first) { break-before: page; }
  tr, .article-head, .totals, h3 { break-inside: avoid; }
  h3 { break-after: avoid; }
  .article-head { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 6px; }
  .product-img { width: 100%; max-width: 320px; height: auto; object-fit: contain; border: 1px solid #ddd; border-radius: 4px; }
  .product-img.placeholder { display: flex; align-items: center; justify-content: center; height: 160px; color: #999; background: #f5f5f5; }
  .article-data { flex: 1; }
  .data-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 14px; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #bbb; padding: 3px 5px; text-align: center; }
  th { background: #f0f0f0; }
  thead { display: table-header-group; }
  .text-left { text-align: left; }
  .num { text-align: right; }
  .strong { font-weight: bold; }
  .matrix .foot td { background: #f0f0f0; font-weight: bold; }
  .swatch { display: inline-block; width: 10px; height: 10px; border-radius: 50%; border: 1px solid #999; margin-right: 5px; vertical-align: middle; }
  .totals { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 8px; }
  .totals div { border: 1px solid #ccc; border-radius: 4px; padding: 4px 10px; }
  .totals .grand { border-color: #1e3a8a; }
  .totals .grand b { color: #1e3a8a; }
</style>
</head>
<body>
  <header class="doc-head">
    <h1>${esc(title)}</h1>
    <div class="doc-meta">
      <div><span class="lbl">Descripción</span><b>${esc(model.description) || "—"}</b></div>
      <div><span class="lbl">Fecha</span>${esc(formatDate(model.simulationDate)) || "—"}</div>
      <div><span class="lbl">Artículos</span>${model.items.length}</div>
      <div><span class="lbl">Observaciones</span>${esc(model.notes) || "—"}</div>
    </div>
    <div class="doc-totals">
      <div><span class="lbl">Total prendas</span><b>${model.totals.total_quantity}</b></div>
      <div><span class="lbl">Total materiales</span><b>${formatMoney(model.totals.material_total)}</b></div>
      <div><span class="lbl">Total procesos</span><b>${formatMoney(model.totals.process_total)}</b></div>
      <div><span class="lbl">Coste total</span><b>${formatMoney(model.totals.grand_total)}</b></div>
    </div>
  </header>
  ${articles}
</body>
</html>`;
}

export function printSimulationModel(model) {
  if (!model?.items?.length) return { ok: false, error: "empty" };
  const printWindow = window.open("", "_blank");
  if (!printWindow) return { ok: false, error: "popup_blocked" };

  printWindow.document.open();
  printWindow.document.write(renderSimulationPrintHtml(model));
  printWindow.document.close();

  const triggerPrint = () => {
    const images = Array.from(printWindow.document.images || []);
    const waitImages = images.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          })
    );
    Promise.all(waitImages).then(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    });
  };

  if (printWindow.document.readyState === "complete") {
    triggerPrint();
  } else {
    printWindow.addEventListener("load", triggerPrint, { once: true });
  }
  return { ok: true };
}
